import '@styles/globals.scss'
import type {AppProps} from 'next/app'
import {NextPage} from "next";
import React, {ReactNode, useEffect, useRef} from "react";
import {useLoadingAnimationOnPageChange, useRouterScroll} from "@utils/useRouterScroll";
import {useRouter} from "next/router";
import {LoadingBar} from "@components/elements/LoadingBar";
import Script from "next/script";
import {GA_MEASUREMENT_ID, trackPageView} from "@utils/analytics";

export type NextPageWithLayout<P = {}, S = {}> = NextPage<P, S> & { // The & m
    getLayout?: (page: React.ReactElement, props?: any) => ReactNode
}

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

export default function App({Component, pageProps}: AppPropsWithLayout) {
    const getLayout = Component.getLayout ?? (page => page)
    const router = useRouter();
    const initialPath = useRef(router.asPath);
    useRouterScroll();
    const isLoading = useLoadingAnimationOnPageChange() || router.isFallback;
    useEffect(() => {
        if (!GA_MEASUREMENT_ID) {
            return;
        }

        const handleRouteChange = (url: string) => {
            const fullUrl = typeof window !== 'undefined' ? new URL(url, window.location.origin).toString() : url;
            trackPageView(fullUrl);
        };

        router.events.on('routeChangeComplete', handleRouteChange);
        handleRouteChange(initialPath.current);

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [router.events]);

    return (<>
        {GA_MEASUREMENT_ID && (
            <>
                <Script
                    src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
                    strategy="afterInteractive"
                />
                <Script id="google-analytics" strategy="afterInteractive">
                    {`
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${GA_MEASUREMENT_ID}', { send_page_view: false });
                    `}
                </Script>
            </>
        )}
        {getLayout(
            <>
                {isLoading && (
                    <div className="sticky top-0 z-30">
                        <LoadingBar/>
                    </div>
                )}
                <Component {...pageProps} />
            </>
            , pageProps)}
    </>)
}
